import axios from 'axios';
import { useUserStore } from '@/store/userStore';
import { useToast } from 'vue-toastification';

const toast = useToast();
const API_BASE_URL = import.meta.env.VITE_APP_API_URL;

// Função para obter o userStore apenas quando necessário
// Isso evita o erro "Cannot read properties of undefined (reading '_s')"
function getUserStore() {
  try {
    // Verificar se estamos no contexto do navegador e se o Pinia está disponível
    if (typeof window !== 'undefined') {
      return useUserStore();
    }
    return { token: null };
  } catch (error) {
    console.error('Erro ao acessar userStore:', error);
    return { token: null };
  }
}

const instance = axios.create({
    baseURL: API_BASE_URL,
    timeout: 300000, // 5 minutos para requisições grandes
    headers: {
      "X-Requested-With": "XMLHttpRequest",
      "Accept": "application/json",
    },
    maxContentLength: 50 * 1024 * 1024, // 50MB
    maxBodyLength: 50 * 1024 * 1024, // 50MB
});

// Interceptor de Requisição
instance.interceptors.request.use(
    config => {
        try {
            // Adicionar token de autenticação, se existir
            const userStore = getUserStore();
            if (userStore && userStore.token) {
                config.headers.Authorization = `Bearer ${userStore.token}`;
            //    console.log('🔑 Token adicionado à requisição:', config.url);
            } else {
                console.warn('⚠️ Nenhum token encontrado para a requisição:', config.url);
            }
        } catch (error) {
            console.error('❌ Erro ao obter token para requisição:', error);
        }
        
        // Log de requisições (opcional, remover em produção)
        // console.log('Enviando requisição:', {
        //     url: config.url,
        //     method: config.method,
        //     headers: config.headers
        // });
        
        return config;
    },
    error => {
        console.error('Erro na configuração da requisição:', error);
        return Promise.reject(error);
    }
);

// Interceptor de Resposta
instance.interceptors.response.use(
    response => {
        // Log de respostas bem-sucedidas (opcional, remover em produção)
        // console.log('Resposta recebida:', {
        //     url: response.config.url,
        //     status: response.status,
        //     data: response.data
        // });
        
        return response;
    },
    error => {
        // Tratamento detalhado de erros
        if (error.response) {
            // O servidor respondeu com um status de erro
            const { status, data } = error.response;
            
            switch (status) {
                case 400:
                    toast.error('Requisição inválida. Verifique os dados enviados.');
                    break;
                case 401:
                    console.warn('🔒 Erro 401 - Token inválido ou expirado');
                    toast.error('Sessão expirada. Faça login novamente.');
                    const userStore = getUserStore();
                    if (userStore && userStore.logout) {
                        userStore.logout(); // Fazer logout completo
                    }
                    // Redirecionar para login (corrigido para /jodarc/)
                    if (typeof window !== 'undefined' && window.location) {
                        setTimeout(() => {
                            window.location.href = '/jodarc/';
                        }, 1500);
                    }
                    break;
                case 403:
                    toast.error('Você não tem permissão para acessar este recurso.');
                    break;
                case 404:
                    toast.error('Recurso não encontrado.');
                    break;
                case 422:
                    // Erro de validação - mostrar detalhes dos campos inválidos
                    console.warn('⚠️ Erro 422 - Validação falhou:', data);
                    
                    if (data?.errors && typeof data.errors === 'object') {
                        // Extrair todas as mensagens de erro dos campos
                        const errorMessages = Object.entries(data.errors)
                            .map(([field, messages]) => {
                                const fieldName = field.replace(/_/g, ' ').replace(/\./g, ' > ');
                                return `${fieldName}: ${Array.isArray(messages) ? messages.join(', ') : messages}`;
                            })
                            .join('\n');
                        
                        toast.error(`Erro de validação:\n${errorMessages}`, { timeout: 5000 });
                    } else if (data?.mensagem || data?.message) {
                        toast.error(data.mensagem || data.message);
                    } else {
                        toast.error('Dados inválidos. Verifique os campos e tente novamente.');
                    }
                    break;
                case 500:
                    toast.error('Erro interno do servidor. Tente novamente mais tarde.');
                    break;
                default:
                    toast.error(`Erro ${status}: ${data?.message || data?.mensagem || 'Erro desconhecido'}`);
            }
            
            // Log detalhado do erro
            let dataEnviada = null;
            try {
                dataEnviada = error.response.config.data ? JSON.parse(error.response.config.data) : null;
            } catch (e) {
                dataEnviada = error.response.config.data;
            }
            
            console.error('🔴 Erro de resposta do servidor:', {
                status: status,
                mensagem: data?.mensagem || data?.message,
                erros: data?.errors,
                url: error.response.config.url,
                method: error.response.config.method?.toUpperCase(),
                data_enviada: dataEnviada
            });
            
            // Log extra para erro 422
            if (status === 422) {
                console.error('⚠️ Detalhes do erro 422:', {
                    validation_errors: data?.errors,
                    message: data?.message || data?.mensagem,
                    full_response: data
                });
            }
        } else if (error.request) {
            // A requisição foi feita, mas não houve resposta
            toast.error('Sem resposta do servidor. Verifique sua conexão.');
            console.error('Sem resposta do servidor:', error.request);
        } else {
            // Algo aconteceu na configuração da requisição
            toast.error('Erro na configuração da requisição.');
            console.error('Erro de configuração:', error.message);
        }
        
        return Promise.reject(error);
    }
);

export default instance;
